import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  endOfMonth,
  endOfWeek,
  isBefore,
  isAfter,
  isSameDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subWeeks,
} from 'date-fns';

import {useCallback} from 'react';

interface UseCalendarKeyboardNavigationProps {
  activeMonth: Date;
  focusedDate: Date | null;
  onFocusChange: (date: Date) => void;
  onDateSelect: (date: Date) => void;
  onMonthChange: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  disabledDates?: Array<Date | [Date, Date]>;
}

interface NavigationHandlers {
  onKeyDown: (event: React.KeyboardEvent) => void;
}

const isDateInRange = (date: Date, range: [Date, Date]): boolean => {
  const [start, end] = range;
  return !isBefore(date, start) && !isAfter(date, end);
};

const isDateDisabled = (
  date: Date,
  minDate?: Date,
  maxDate?: Date,
  disabledDates?: Array<Date | [Date, Date]>
): boolean => {
  if (minDate && isBefore(date, minDate)) {
    return true;
  }

  if (maxDate && isAfter(date, maxDate)) {
    return true;
  }

  if (disabledDates) {
    for (const disabledDate of disabledDates) {
      if (Array.isArray(disabledDate)) {
        // eslint-disable-next-line max-depth -- Pre-existing violation
        if (isDateInRange(date, disabledDate)) {
          return true;
        }
      } else if (isSameDay(date, disabledDate)) {
        return true;
      }
    }
  }

  return false;
};

const findNextAvailableDate = (
  startDate: Date,
  direction: number,
  minDate?: Date,
  maxDate?: Date,
  disabledDates?: Array<Date | [Date, Date]>
): Date => {
  // First check if the start date itself is valid
  if (!isDateDisabled(startDate, minDate, maxDate, disabledDates)) {
    return startDate;
  }

  let candidateDate = startDate;
  const maxAttempts = 366; // Prevent infinite loops
  let attempts = 0;

  while (attempts < maxAttempts) {
    candidateDate = addDays(candidateDate, direction);

    // Check if we've gone beyond the allowed range
    if (minDate && isBefore(candidateDate, minDate)) {
      return minDate;
    }
    if (maxDate && isAfter(candidateDate, maxDate)) {
      return maxDate;
    }

    if (!isDateDisabled(candidateDate, minDate, maxDate, disabledDates)) {
      return candidateDate;
    }

    attempts++;
  }

  return startDate; // Fallback to original date if no valid date found
};

export function useCalendarKeyboardNavigation(
  props: UseCalendarKeyboardNavigationProps
): NavigationHandlers {
  const {
    activeMonth,
    focusedDate,
    onFocusChange,
    onDateSelect,
    onMonthChange,
    minDate,
    maxDate,
    disabledDates,
  } = props;

  const handleNavigateDate = useCallback(
    (newDate: Date, shouldChangeMonth = false) => {
      const availableDate = findNextAvailableDate(newDate, 1, minDate, maxDate, disabledDates);

      if (shouldChangeMonth && focusedDate && !isSameDay(availableDate, focusedDate)) {
        onMonthChange(startOfMonth(availableDate));
      }

      onFocusChange(availableDate);
    },
    [focusedDate, minDate, maxDate, disabledDates, onFocusChange, onMonthChange]
  );

  const onKeyDown = useCallback(
    // eslint-disable-next-line max-statements -- Pre-existing violation
    (event: React.KeyboardEvent) => {
      if (!focusedDate) {
        return;
      }

      const {key, ctrlKey, metaKey} = event;
      const isModifierPressed = ctrlKey || metaKey;

      switch (key) {
        // Arrow key navigation
        case 'ArrowRight': {
          event.preventDefault();
          const nextDay = addDays(focusedDate, 1);
          handleNavigateDate(nextDay, !isSameDay(startOfMonth(nextDay), activeMonth));
          break;
        }

        case 'ArrowLeft': {
          event.preventDefault();
          const prevDay = subDays(focusedDate, 1);
          handleNavigateDate(prevDay, !isSameDay(startOfMonth(prevDay), activeMonth));
          break;
        }

        case 'ArrowDown': {
          event.preventDefault();
          const nextWeek = addWeeks(focusedDate, 1);
          handleNavigateDate(nextWeek, !isSameDay(startOfMonth(nextWeek), activeMonth));
          break;
        }

        case 'ArrowUp': {
          event.preventDefault();
          const prevWeek = subWeeks(focusedDate, 1);
          handleNavigateDate(prevWeek, !isSameDay(startOfMonth(prevWeek), activeMonth));
          break;
        }

        // Page navigation
        case 'PageDown': {
          event.preventDefault();
          if (isModifierPressed) {
            // Ctrl/Cmd + PageDown = next year
            const nextYear = addYears(focusedDate, 1);
            handleNavigateDate(nextYear, true);
          } else {
            // PageDown = next month
            const nextMonth = addMonths(focusedDate, 1);
            handleNavigateDate(nextMonth, true);
          }
          break;
        }

        case 'PageUp': {
          event.preventDefault();
          if (isModifierPressed) {
            // Ctrl/Cmd + PageUp = previous year
            const prevYear = addYears(focusedDate, -1);
            handleNavigateDate(prevYear, true);
          } else {
            // PageUp = previous month
            const prevMonth = addMonths(focusedDate, -1);
            handleNavigateDate(prevMonth, true);
          }
          break;
        }

        // Home/End navigation
        case 'Home': {
          event.preventDefault();
          if (isModifierPressed) {
            // Ctrl/Cmd + Home = start of month
            const monthStart = startOfMonth(focusedDate);
            handleNavigateDate(monthStart);
          } else {
            // Home = start of week
            const weekStart = startOfWeek(focusedDate);
            handleNavigateDate(weekStart);
          }
          break;
        }

        case 'End': {
          event.preventDefault();
          if (isModifierPressed) {
            // Ctrl/Cmd + End = end of month
            const monthEnd = endOfMonth(focusedDate);
            handleNavigateDate(monthEnd);
          } else {
            // End = end of week
            const weekEnd = endOfWeek(focusedDate);
            handleNavigateDate(weekEnd);
          }
          break;
        }

        // Date selection
        case 'Enter':
        case ' ': {
          event.preventDefault();
          if (!isDateDisabled(focusedDate, minDate, maxDate, disabledDates)) {
            onDateSelect(focusedDate);
          }
          break;
        }

        default:
          // Don't prevent default for other keys
          break;
      }
    },
    [focusedDate, activeMonth, handleNavigateDate, onDateSelect, minDate, maxDate, disabledDates]
  );

  return {
    onKeyDown,
  };
}
